import TvApi from '../api/TvApi';
import { executeHttpRequest } from '../infra/HttpClient';
import { cacheAsyncMethod } from '../infra/cache/CacheUtils';
import { each, sortBy, values } from 'lodash';
import { CHANNELS } from '../../data/audience-segment-builder-helper';
export function getGenres() {
  return executeHttpRequest(TvApi.getGenres());
}

export function getNetworks(channel) {
  return executeHttpRequest(TvApi.getNetworks(channel));
}

export function getShows(channel) {
  return executeHttpRequest(TvApi.getShows(channel));
}

export function getCommercials(channel) {
  return executeHttpRequest(TvApi.getCommercials(channel));
}

export function getCommercialsV2(channel, parent_brands, brands, products, geo, source, categories) {
  const hasCategoryFilter = channel === CHANNELS?.hisense?.name && categories && categories.length;
  const payload = {
    brand_parent_ids: parent_brands || [],
    brand_ids: hasCategoryFilter ? [] : brands || [],
    product_ids: products || [],
    geo: geo || '',
    ...(channel === CHANNELS?.hisense?.name && {
      source: source || '',
      category_ids: categories || [],
    }),
  };

  return executeHttpRequest(TvApi.getCommercialsV2(channel, payload));
}

export function getHierarchy(channel) {
  return executeHttpRequest(TvApi.getHierarchy(channel));
}

export function getShowsMetadataByids(ids, channel) {
  return executeHttpRequest(TvApi.getShowsMetadataByids(ids, channel));
}

export function getNielsenNetworksMap() {
  return executeHttpRequest(TvApi.getNielsenNetworksMap());
}

export function getKeywords(channel, queryParams, geo) {
  return executeHttpRequest(TvApi.getKeyWords(channel, queryParams, geo));
}

export async function getNetworksShows(networkIds, channel) {
  const networksShows = await executeHttpRequest(TvApi.getNetworksShows(networkIds, channel));
  return new Set(networksShows);
}

export function getAdvancedShows(channel, networks, genres, geo) {
  const promise = executeHttpRequest(TvApi.getAdvancedShows(channel, networks, genres, geo), {
    cancelOldRequest: true,
  });
  return promise.then((data) => transformAdvancedTvShowsFromApi(data.shows));
}

// ---------------------------------------------------------------------------
// v3 contract: POST /tv/v3/advanced/:channel & POST /tv/v3/advanced-search/:channel
// ---------------------------------------------------------------------------

export function transformV3AdvancedShowsFromApi(shows) {
  return sortBy(
    (shows || []).map((show) => ({
      id: show.series_id,
      title: show.series_title,
      is_movie: show.is_movie,
      source: show.source,
      seasons: groupProgramsBySeason(show.children || []),
    })),
    'title'
  );
}

function groupProgramsBySeason(programs) {
  const bySeason = {};
  programs.forEach((program) => {
    const seasonKey = program.season_number == null ? '__none__' : String(program.season_number);
    if (!bySeason[seasonKey]) {
      bySeason[seasonKey] = {
        id: program.season_number,
        title: program.season_number ? `S${program.season_number}` : '(no season)',
        isSeason: !!program.season_number,
        episodes: [],
      };
    }
    const titlePrefix = program.date || '';
    bySeason[seasonKey].episodes.push({
      id: program.program_id,
      title: titlePrefix + (titlePrefix && program.program_title ? ' - ' : '') + (program.program_title || ''),
      episode_number: program.episode_number,
      date: program.date,
      source: program.source,
    });
  });
  values(bySeason).forEach((season) => {
    season.episodes = sortBy(season.episodes, ['episode_number', 'title', 'date']);
  });
  return sortBy(values(bySeason), (season) => (season.id == null ? Number.MAX_SAFE_INTEGER : Number(season.id)));
}

export function getAdvancedShowsBatch(channel, viewingType, networks, genres, geo, offset, limit, sources) {
  return executeHttpRequest(
    TvApi.getAdvancedShowsBatch(channel, viewingType, offset, limit, networks, genres, geo, sources),
    { displayRedBannerOnError: false }
  ).then((data) => ({
    shows: transformV3AdvancedShowsFromApi(data.shows),
    hasMore: !!data.pagination?.has_more,
    nextCursor: data.pagination?.next_cursor || null,
  }));
}

export function searchAdvancedShows(
  channel,
  viewingType,
  networks,
  genres,
  geo,
  query,
  offset = 0,
  limit = 50,
  scope,
  exactMatch,
  sources
) {
  return executeHttpRequest(
    TvApi.searchAdvancedShows(
      channel,
      viewingType,
      query,
      offset,
      limit,
      networks,
      genres,
      geo,
      scope,
      exactMatch,
      sources
    ),
    {
      cancelOldRequest: true,
      displayRedBannerOnError: false,
    }
  ).then((data) => ({
    shows: transformV3AdvancedShowsFromApi(data.shows),
    hasMore: !!data.pagination?.has_more,
    nextCursor: data.pagination?.next_cursor || null,
  }));
}

export function getAdvancedGenres() {
  return executeHttpRequest(TvApi.getAdvancedGenres());
}

export function getAdvancedNetworks(channel, geo) {
  return executeHttpRequest(TvApi.getAdvancedNetworks(channel, geo));
}

export function transformAdvancedTvShowsFromApi(shows) {
  let res = {};
  each(shows, (show) => {
    res[show.series_id] = res[show.series_id] || {
      id: show.series_id,
      title: show.series_title,
      is_movie: show.is_movie,
      seasons: {},
    };
    if (!show.is_movie) {
      res[show.series_id].seasons[show.season_number] = res[show.series_id].seasons[show.season_number] || {
        id: show.season_number,
        title: show.season_number ? `S${show.season_number}` : '(no season)',
        isSeason: !!show.season_number,
        episodes: {},
      };
      const titlePrefix = show.date || '';
      res[show.series_id].seasons[show.season_number].episodes[show.program_id] = {
        id: show.program_id,
        title: titlePrefix + (titlePrefix && show.program_title ? ' - ' : '') + (show.program_title || ''),
        episode_number: show.episode_number,
        date: show.release_date,
      };
    }
  });
  each(res, (show) => {
    each(
      show.seasons,
      (season) => (season.episodes = sortBy(values(season.episodes), ['episode_number', 'title', 'date']))
    );
    show.seasons = sortBy(values(show.seasons), 'season_number');
  });
  return sortBy(values(res), 'title');
}

// Cache

export const getCachedGenres = cacheAsyncMethod('TvService.getGenres', getGenres);

export const getCachedNetworks = cacheAsyncMethod('TvService.getNetworks', getNetworks, {
  cacheSingleEntryForMethod: false,
});

export const getCachedHierarchy = cacheAsyncMethod('TvService.getHierarchy', getHierarchy, {
  cacheSingleEntryForMethod: false,
});

export const getCachedShows = cacheAsyncMethod('TvService.getShows', getShows, {
  cacheSingleEntryForMethod: false,
});

export const getCachedCommercials = cacheAsyncMethod('TvService.getCommercials', getCommercials, {
  cacheSingleEntryForMethod: false,
});

export const getCachedCommercialsV2 = cacheAsyncMethod('TvService.getCommercialsV2', getCommercialsV2, {
  cacheSingleEntryForMethod: false,
});

export const getCachedShowsMetadataByids = cacheAsyncMethod('TvService.getShowsMetadataByids', getShowsMetadataByids);

export const getCachedNetworksShows = cacheAsyncMethod('TvService.getNetworksShows', getNetworksShows, {
  cacheSingleEntryForMethod: false,
});

export const getCachedAdvancedShows = cacheAsyncMethod('TvService.getAdvancedShows', getAdvancedShows, {
  cacheSingleEntryForMethod: false,
});

export const getCachedAdvancedGenres = cacheAsyncMethod('TvService.getAdvancedGenres', getAdvancedGenres);

export const getCachedAdvancedNetworks = cacheAsyncMethod('TvService.getAdvancedNetworks', getAdvancedNetworks, {
  cacheSingleEntryForMethod: false,
});

export const getCachedAdvancedShowsBatch = cacheAsyncMethod('TvService.getAdvancedShowsBatch', getAdvancedShowsBatch, {
  cacheSingleEntryForMethod: false,
});

export const getCachedAdvancedShowsSearch = cacheAsyncMethod('TvService.searchAdvancedShows', searchAdvancedShows, {
  cacheSingleEntryForMethod: false,
});
